import { Middleware } from '@reduxjs/toolkit';
import { Donation, PaymentModuleState, Subscription } from '../../../types/PaymentTypes';
import { EVENTS, sendEvent } from '../../analytics';
import { captureException } from '../../tracking/sentry/sentry';
import { RootState } from '../store';
import { startPaymentFlow } from './commonPaymentActions';
import { updateSubscription } from './paymentAsyncActions';
import { getActiveDonation, getActiveSubscription, getPaymentFlowId } from './paymentSelectors';

const analyticsMiddleware: Middleware = ({ getState }: { getState: () => RootState }) => (
  next,
) => async (action) => {
  const state = getState();
  const nextAction = next(action);

  const paymentFlowId = getPaymentFlowId(state);

  if (!paymentFlowId) {
    return nextAction;
  }

  try {
    if (action.type === startPaymentFlow.type) {
      const { donation, subscription } = getCurrentState(state);
      const property = createAnalyticsEventProperties({ donation, subscription, paymentFlowId });

      await handleSendEvent({
        name: EVENTS.startPaymentFlow.name,
        eventProperties: property,
      });
    }

    if (action.type === updateSubscription.pending.type) {
      const { donation, subscription } = getCurrentState(state);
      const property = createAnalyticsEventProperties({ donation, subscription, paymentFlowId });

      await handleSendEvent({
        name: EVENTS.supportClickConfirm.name,
        eventProperties: property,
      });
    }

    if (action.type === updateSubscription.rejected.type) {
      const { donation, subscription } = getCurrentState(state);
      const property = createAnalyticsEventProperties({ donation, subscription, paymentFlowId });

      await handleSendEvent({
        name: EVENTS.supportFailed.name,
        eventProperties: {
          ...property,
          error: subscription?.errorMessage,
        },
      });
    }

    if (action.type === updateSubscription.fulfilled.type) {
      const { donation, subscription } = getCurrentState(state);
      const property = createAnalyticsEventProperties({ donation, subscription, paymentFlowId });

      await handleSendEvent({
        name: EVENTS.supportSuccessful.name,
        eventProperties: property,
      });
    }
  } catch (error) {
    captureException(error);
  }

  return nextAction;
};

type EventProps = {
  name: string;
  eventProperties?: Record<string, unknown>;
};

type State = {
  donation?: Donation;
  subscription?: Subscription;
  paymentFlowId?: string;
  target?: string;
};

const getCurrentState = (state: PaymentModuleState): State => {
  return {
    donation: getActiveDonation(state),
    subscription: getActiveSubscription(state),
    paymentFlowId: state.payment.paymentFlowId,
  };
};

const createAnalyticsEventProperties = ({
  paymentFlowId,
  donation,
  subscription,
  target,
}: State) => {
  if (!paymentFlowId) {
    return;
  }
  return {
    initiator: donation?.target || target || 'donate page',
    'payment flow id': paymentFlowId,
    'flow type': 'subscription',
    'support type': donation?.plan,
    amount: subscription?.details?.currentTotalAmount,
    currencyCode: subscription?.details?.currencyCode,
    'payment provider': donation?.provider,
    'payment method': donation?.provider === 'stripe' ? 'card' : '',
    'impacter ids': donation?.impacterIds,
    'tag names': donation?.tagNames,
  };
};

const handleSendEvent = async ({ name, eventProperties }: EventProps) => {
  await sendEvent(name, {
    'support type': 'monthly',
    'flow type': 'subscription',
    ...eventProperties,
  });
};

export default analyticsMiddleware;
